/**
 * 房间级的聊天副作用，挂在 ActionDockBinding 里（整个房间期间常驻）：
 *  - 换房间清空本机聊天态；第一次看到房间时，把已有的历史算作「已读」；
 *  - 新话进空闲条快讯（4 秒）；
 *  - 被催：只在「目标是我、我持有行动权、正是当前这一步、同一步第一次」时震一下、抖一下计时环；
 *  - 认领被拒的聊天帧：限流 → 抽屉里提示并把字还回去；关了聊天 → 提示。
 *    其它地方的错误提示据 `isTalkCid` 跳过这些帧，免得弹两遍。
 */
import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import type { ActionPrompt, ServerFrame, ServerMessage } from '@poker/shared'

import { fireHaptic } from '../fx/haptics.js'
import { playPhraseVoice } from '../fx/sampleSfx.js'
import { useRoomLink } from '../room/roomLink.js'
import { useSettingsStore, useUiStore } from '../store/index.js'
import { useNetStore } from '../transport/applyMessage.js'
import { isFreshEvent, latestAt, nudgeKey } from './talkDerive.js'
import { useTalkStore } from './talkStore.js'

/** 快讯停留时长 */
export const TICKER_MS = 4000

export interface TalkFeedOptions {
  readonly ringRef: RefObject<HTMLElement | null>
  readonly promptRef: RefObject<ActionPrompt | null>
  readonly isActorRef: RefObject<boolean>
}

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

/** 计时环抖三下：只动 transform（红线 #46）；减弱动效时改成描边闪一下 */
export function knockRing(el: HTMLElement | null): void {
  if (el === null) return
  if (prefersReducedMotion() || typeof el.animate !== 'function') {
    el.classList.add('is-nudged')
    setTimeout(() => el.classList.remove('is-nudged'), 600)
    return
  }
  el.animate(
    [
      { transform: 'translateX(0)' },
      { transform: 'translateX(-3px) rotate(-8deg)' },
      { transform: 'translateX(3px) rotate(8deg)' },
      { transform: 'translateX(-2px) rotate(-5deg)' },
      { transform: 'translateX(0)' },
    ],
    { duration: 360, easing: 'ease-in-out' },
  )
}

export function useTalkFeed({ ringRef, promptRef, isActorRef }: TalkFeedOptions): void {
  const link = useRoomLink()
  const roomId = useNetStore((s) => s.view?.room.id ?? null)
  const hasView = useNetStore((s) => s.view !== null)
  const lastError = useNetStore((s) => s.lastError)
  const clearError = useNetStore((s) => s.clearError)
  const showToast = useUiStore((s) => s.showToast)
  const buzzedRef = useRef<string | null>(null)

  // ── 换房间：清空；进房时已有的历史算已读 ──
  useEffect(() => {
    useTalkStore.getState().reset()
    buzzedRef.current = null
  }, [roomId])
  // ★ 离开房间时这一整条绑定直接卸载，上面那个 roomId effect 根本跑不到；
  //   而下次挂载时子组件的 effect 先于这里执行，会先拿到上一个房间残留的 open / composing
  useEffect(() => () => useTalkStore.getState().reset(), [])
  useEffect(() => {
    if (!hasView) return
    const s = useTalkStore.getState()
    if (s.seenAt === null) s.markSeen(latestAt(useNetStore.getState().chat))
  }, [hasView, roomId])

  // ── 新话 → 快讯；被催 → 震动 ──
  useEffect(() => {
    const onMsg = (m: ServerMessage, frame: ServerFrame): void => {
      if (m.type !== 'chat' && m.type !== 'emote') return
      if (useUiStore.getState().resync) return
      if (!isFreshEvent(frame.st, link.clock.now())) return
      const view = useNetStore.getState().view
      if (view === null) return
      const me = view.viewer.accountId
      const mySeat = view.viewer.ownedSeat
      const talk = useTalkStore.getState()

      if (m.type === 'chat') {
        if (m.entry.kind !== 'player') return
        talk.pushTicker(m.entry.accountId === me ? '我' : m.entry.nickname, m.entry.text)
        // 自己的短语在点击时已经播过（TalkDrawer），这里只播别人的
        if (m.entry.accountId !== me && m.entry.phrase !== undefined) {
          playPhraseVoice(m.entry.phrase, m.entry.voice, useSettingsStore.getState().sound)
        }
        return
      }
      if (m.emote !== 'nudge' || m.targetSeat === undefined) return
      const from = m.accountId === me ? '我' : (view.seats.find((s) => s.occupant?.accountId === m.accountId)?.occupant?.nickname ?? '有人')
      const target = view.seats[m.targetSeat]?.occupant?.nickname ?? ''
      talk.pushTicker(from, m.targetSeat === mySeat ? '催你呢' : `催了 ${target}`)

      if (m.targetSeat !== mySeat || !isActorRef.current) return
      const p = promptRef.current
      if (p === null || m.handId !== p.handId || m.actionNo !== p.actionNo) return
      const key = nudgeKey(p.handId, p.actionNo)
      if (buzzedRef.current === key) return
      buzzedRef.current = key
      fireHaptic('nudge', useSettingsStore.getState().haptics, ringRef.current)
      knockRing(ringRef.current)
    }
    const subscribe = link.onApplied ?? link.onMessage
    if (subscribe === undefined) return undefined
    return subscribe(onMsg)
  }, [link, ringRef, promptRef, isActorRef])

  // ── 认领被拒的聊天帧 ──
  useEffect(() => {
    if (lastError === null) return
    const talk = useTalkStore.getState()
    if (!talk.isTalkCid(lastError.refCid)) return
    const text =
      lastError.code === 'E_RATE_LIMITED'
        ? '说太快了，等一下'
        : lastError.code === 'E_ILLEGAL_ACTION'
          ? '房主关了聊天'
          : '这句没发出去，再试一次'
    // 手打的那句被拒：字还给你（输入框空着才还，别覆盖你已经在打的下一句）
    if (talk.lastText !== null && talk.lastText.cid === lastError.refCid && talk.draft === '') {
      talk.setDraft(talk.lastText.text)
    }
    if (talk.open) talk.setNotice(text)
    else showToast(text, 'warn', 1500)
    clearError()
  }, [lastError, clearError, showToast])
}
